import { useState } from "react";
import type { HpChangeKind } from "@dm/shared";

export function HpBar({ hp, maxHp, tempHp }: { hp: number; maxHp: number; tempHp: number }) {
  const pct = maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
  const tone = hp <= 0 ? "down" : pct <= 25 ? "low" : pct <= 50 ? "mid" : "ok";
  return (
    <div className="hp">
      <div className="hp-bar">
        <div className={`hp-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="hp-text">
        {hp}/{maxHp}
        {tempHp > 0 && <span className="temp"> +{tempHp}</span>}
      </span>
    </div>
  );
}

/** Amount box + Damage / Heal / Temp buttons. */
export function HpControls({ onChange, disabled }: { onChange: (kind: HpChangeKind, amount: number) => Promise<void>; disabled?: boolean }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Math.max(0, Math.floor(Number(amount)));
  const go = async (kind: HpChangeKind) => {
    if (!n) return;
    setBusy(true);
    try {
      await onChange(kind, n);
      setAmount("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hp-controls">
      <input
        type="number"
        min={0}
        placeholder="HP"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && go("damage")}
        aria-label="Amount"
      />
      <button className="dmg" disabled={disabled || busy || !n} onClick={() => go("damage")} title="Damage (temp HP first)">
        Dmg
      </button>
      <button className="heal" disabled={disabled || busy || !n} onClick={() => go("heal")}>
        Heal
      </button>
      <button disabled={disabled || busy || !n} onClick={() => go("temp")} title="Set temporary HP">
        Temp
      </button>
    </div>
  );
}

export function DeathSaves({ successes, failures }: { successes: number; failures: number }) {
  return (
    <span className="death-saves" title={`Death saves: ${successes} successes, ${failures} failures`}>
      {[0, 1, 2].map((i) => (
        <span key={`s${i}`} className={`pip ok ${i < successes ? "on" : ""}`} />
      ))}
      <span className="sep" />
      {[0, 1, 2].map((i) => (
        <span key={`f${i}`} className={`pip bad ${i < failures ? "on" : ""}`} />
      ))}
    </span>
  );
}
